import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import EmptyState from '../EmptyState';
import LoadErrorBox from '../ui/LoadErrorBox';
import { useConfirm } from '../ui/ConfirmProvider';
import { FormInput, FormSelect, FormTextarea } from '../FormField';
import { useNotifications } from '../NotificationProvider';
import { useClearErrorsOnEdit, useFormDeleteEvent, useFormSaveEvent } from '../forms/_shared';
import { GIFT_FOREVER_EVENT, GIFT_RESTORE_EVENT, NO_GIFT_EDITOR, setGiftRegistryBar } from '../../hooks/useGiftRegistryBar';
import { useLoad } from '../../hooks/useLoad';
import { useMoney } from '../../hooks/useMoney';
import { usePageRefresh } from '../../hooks/usePageRefresh';
import { useRole } from '../../hooks/useRole';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { getProjectId } from '../../services/PlanningApiClient';
import {
  archiveGiftItem, createGiftItem, deleteGiftItemForever, fetchGiftItemByRef, removeGiftReservation, restoreGiftItem, updateGiftItem,
} from '../../services/GiftRegistryService';
import { resolveMediaUrl, uploadImage } from '../../services/uploadService';
import { errorMessageOf } from '../../utils/apiErrors';
import { formatDateTime } from '../../utils/formatDate';
import {
  GIFT_KIND_OPTIONS, GIFT_LIST_PATH, GIFT_PRIORITY_OPTIONS, buildGiftPayload, emptyGiftForm, giftFormChanged, giftFormFromItem,
  reservedSummary, validateGiftForm, type GiftForm, type GiftFormErrors,
} from '../../utils/giftRegistry';
import type { GiftItem, GiftKind, GiftPriority } from '../../types/GiftRegistry';
import '../../styles/components/FormPage.css';
import '../../styles/components/GiftRegistry.css';

interface Props {
  /** The record number from the address (GR-7 -> 7); omit for a new item. */
  refNumber?: number;
}

/** One item: the standard form page (details on the left, photo and reservations beside it). Save / Cancel / Archive sit in the Header's bar. */
const GiftItemPage: React.FC<Props> = ({ refNumber }) => {
  const isNew = refNumber === undefined;
  const load = useLoad(() => (isNew ? Promise.resolve(null) : fetchGiftItemByRef(refNumber as number)), [refNumber]);
  usePageRefresh(() => load.reload(), load.loading);

  // While the item is loading, missing or failed there is nothing to save: the bar shows no actions.
  const showing = isNew || !!load.data;
  useEffect(() => {
    if (!showing) setGiftRegistryBar({ editor: NO_GIFT_EDITOR });
  }, [showing]);
  useEffect(() => () => setGiftRegistryBar({ editor: NO_GIFT_EDITOR }), []);

  if (load.error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="this item" error={load.error} onRetry={load.reload} retrying={load.loading} /></div>;
  }
  if (load.loading && !load.data && !isNew) {
    return <div className="wt-container fade-in"><p className="gr-note" role="status">Loading the item...</p></div>;
  }
  if (!isNew && !load.data) {
    return (
      <div className="wt-container fade-in">
        <EmptyState icon="gift" title="This item is not in the registry" description="It may have been deleted, or the address is wrong."
          action={<Link className="primary-btn" to={GIFT_LIST_PATH}>Back to the registry</Link>} />
      </div>
    );
  }
  return <GiftItemForm key={load.data?.id ?? 'new'} item={load.data} reload={load.reload} />;
};

interface FormProps { item: GiftItem | null; reload: () => Promise<void> }

const GiftItemForm: React.FC<FormProps> = ({ item, reload }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const money = useMoney();
  const { canEdit, canViewFinancials } = useRole();
  const { showSuccess, showError } = useNotifications();
  const isNew = item === null;
  const archived = !!item?.archived;
  const readOnly = !canEdit || archived;

  const [form, setForm] = useState<GiftForm>(() => (item ? giftFormFromItem(item) : emptyGiftForm()));
  const [problems, setProblems] = useState<GiftFormErrors>({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const currency = item?.currency || money.currency;
  const priceUnusable = canViewFinancials && !money.isSet && !(item?.price);
  const sendPrice = canViewFinancials && !priceUnusable;

  const dirty = !readOnly && giftFormChanged(form, item);
  useUnsavedChangesGuard(dirty);
  const goList = () => navigate(GIFT_LIST_PATH);

  useEffect(() => {
    setGiftRegistryBar({ editor: { canEdit, isNew, archived, dirty, saving } });
  }, [canEdit, isNew, archived, dirty, saving]);

  const save = async () => {
    if (readOnly) return;
    const found = validateGiftForm(form, { canViewFinancials: sendPrice, reserved: item?.reserved_quantity });
    setProblems(found);
    if (Object.keys(found).length > 0) {
      showError('Please check the highlighted fields.');
      return;
    }
    const payload = buildGiftPayload(form, { canViewFinancials: sendPrice });
    setSaving(true);
    try {
      if (item) await updateGiftItem(item.id, payload);
      else await createGiftItem(payload);
    } finally {
      setSaving(false);
    }
    showSuccess(item ? 'Item saved' : 'Item added');
    goList();
  };
  // A failed save throws: the shared hook shows the API's field errors under their fields, or a toast.
  const fieldErrors = useFormSaveEvent(save, { fallback: 'The item could not be saved. Please try again.' });
  useClearErrorsOnEdit(form as unknown as Record<string, unknown>, fieldErrors, {});
  const errorOf = (field: keyof GiftForm) => problems[field] ?? fieldErrors.errorFor(field);
  const set = <K extends keyof GiftForm>(key: K, value: GiftForm[K]) => {
    setForm(f => ({ ...f, [key]: value }));
    setProblems(p => (p[key] ? { ...p, [key]: undefined } : p));
  };

  const archive = async () => {
    if (!item || archived) return;
    const ok = await confirm({
      title: 'Archive this item?', subject: item.title,
      description: 'Guests stop seeing it. Reservations are kept, and you can restore it from the Archived list.',
      confirmLabel: 'Archive', variant: 'danger',
    });
    if (!ok) return;
    try { await archiveGiftItem(item.id); showSuccess(`"${item.title}" archived`); goList(); } catch (err) { showError(errorMessageOf(err, 'The item could not be archived.')); }
  };
  const restore = async () => {
    if (!item) return;
    try { await restoreGiftItem(item.id); showSuccess(`"${item.title}" restored`); await reload(); } catch (err) { showError(errorMessageOf(err, 'The item could not be restored.')); }
  };
  const forever = async () => {
    if (!item) return;
    const ok = await confirm({
      title: 'Delete this item forever?', subject: item.title, description: 'This cannot be undone.',
      consequences: ['The item and its details', 'Its past reservations'], confirmLabel: 'Delete forever', variant: 'danger',
    });
    if (!ok) return;
    try { await deleteGiftItemForever(item.id); showSuccess(`"${item.title}" deleted`); goList(); } catch (err) { showError(errorMessageOf(err, 'The item could not be deleted.')); }
  };

  useFormDeleteEvent(() => { void archive(); });
  const handlers = useRef({ restore, forever });
  handlers.current = { restore, forever };
  useEffect(() => {
    const onRestore = () => { void handlers.current.restore(); };
    const onForever = () => { void handlers.current.forever(); };
    window.addEventListener(GIFT_RESTORE_EVENT, onRestore);
    window.addEventListener(GIFT_FOREVER_EVENT, onForever);
    return () => { window.removeEventListener(GIFT_RESTORE_EVENT, onRestore); window.removeEventListener(GIFT_FOREVER_EVENT, onForever); };
  }, []);

  const upload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Photos are stored as project files (the project is the owner); the item keeps the address.
      const att = await uploadImage(file, { entityType: 'project', entityId: getProjectId(), label: 'gift-registry' });
      set('photo_url', att.url);
    } catch (err) {
      showError(errorMessageOf(err, 'The photo could not be uploaded. Please try again.'));
    } finally {
      setUploading(false);
    }
  };

  const removeReservation = async (id: string, name: string) => {
    if (!item || removing) return;
    const ok = await confirm({
      title: 'Remove this reservation?', subject: name,
      description: 'The item becomes available to guests again.',
      confirmLabel: 'Remove', variant: 'danger',
    });
    if (!ok) return;
    setRemoving(id);
    try { await removeGiftReservation(item.id, id); showSuccess('Reservation removed'); await reload(); } catch (err) { showError(errorMessageOf(err, 'The reservation could not be removed.')); } finally { setRemoving(null); }
  };

  const photo = form.photo_url.trim();

  return (
    <div className="wt-container fade-in">
      <div className="detail-layout">
        <form className="inline-form-page" onSubmit={e => { e.preventDefault(); window.dispatchEvent(new CustomEvent('form-save')); }} noValidate>
          {archived && <div className="entity-state-banner entity-state-banner--locked">Archived. Restore it from the bar to edit it, or delete it forever.</div>}
          {!canEdit && <div className="entity-state-banner">You can look at this item, but only a planner or owner can change it.</div>}
          <div className="form-row">
            <FormInput label="Item name" type="text" value={form.title} required disabled={readOnly} maxLength={200}
              placeholder="e.g. Stand mixer" className="sfp-form-group-flex2"
              error={errorOf('title')} onChange={e => set('title', e.target.value)} />
            <FormSelect label="Type" value={form.kind} disabled={readOnly} options={GIFT_KIND_OPTIONS}
              help="A gift is something to buy. A request is anything you ask for, like help or a contribution."
              error={errorOf('kind')} onChange={e => set('kind', e.target.value as GiftKind)} />
          </div>
          <FormTextarea label="Description" rows={4} value={form.description} disabled={readOnly} maxLength={2000}
            placeholder="Colour, size, why you would love it"
            error={errorOf('description')} onChange={e => set('description', e.target.value)} />
          <div className="form-section">
            <p className="form-section-title">Details</p>
            <div className="form-row">
              <FormInput label="How many" type="number" inputMode="numeric" min={1} max={10000} value={form.quantity_wanted} disabled={readOnly}
                error={errorOf('quantity_wanted')} onChange={e => set('quantity_wanted', e.target.value)} />
              <FormSelect label="Priority" value={form.priority} disabled={readOnly} options={GIFT_PRIORITY_OPTIONS}
                error={errorOf('priority')} onChange={e => set('priority', e.target.value as GiftPriority)} />
            </div>
            <div className="form-row">
              <FormInput label="Link to the item" type="url" value={form.link_url} disabled={readOnly} placeholder="https://shop.example/item"
                help="Where guests can see or buy it."
                error={errorOf('link_url')} onChange={e => set('link_url', e.target.value)} />
              {canViewFinancials && (
                <FormInput label={`Price (${currency || 'currency not set'})`} type="text" inputMode="decimal" value={form.price}
                  disabled={readOnly || priceUnusable} placeholder="0.00"
                  help={priceUnusable ? 'Set the project currency in Settings to add a price. Only you see prices, guests do not.' : 'Only you see the price, guests do not.'}
                  error={errorOf('price')} onChange={e => set('price', e.target.value)} />
              )}
            </div>
          </div>
        </form>

        <aside className="detail-sidebar">
          <section className="side-section">
            <h4>Photo</h4>
            {photo !== '' && <img className="gr-photo" src={resolveMediaUrl(photo)} alt={form.title ? `Photo of ${form.title}` : 'Item photo'} />}
            {!readOnly && (
              <>
                <div className="gr-photo-actions">
                  <button type="button" className="header-action-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    {uploading ? 'Uploading...' : photo ? 'Replace photo' : 'Upload photo'}
                  </button>
                  {photo !== '' && <button type="button" className="header-action-btn" onClick={() => set('photo_url', '')}>Remove photo</button>}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="u-hidden" aria-label="Choose a photo"
                  onChange={e => { void upload(e.target.files); e.target.value = ''; }} />
                <FormInput label="Or a link to a photo" type="url" value={form.photo_url.startsWith('/uploads/') ? '' : form.photo_url}
                  placeholder="https://..." error={errorOf('photo_url')} onChange={e => set('photo_url', e.target.value)} />
              </>
            )}
            {readOnly && photo === '' && <p className="gr-note">No photo.</p>}
          </section>

          {item && (
            <section className="side-section" aria-labelledby="gr-reservations-title">
              <h4 id="gr-reservations-title">Reservations</h4>
              <div className="stat-row"><span>Wanted</span><span>{item.quantity_wanted}</span></div>
              <div className="stat-row"><span>Reserved</span><span>{item.reserved_quantity}</span></div>
              <div className="stat-row"><span>Still wanted</span><span>{item.remaining_quantity}</span></div>
              <p className="gr-note">{reservedSummary(item)}. Guests only see "Reserved" or "Still wanted", never these names.</p>
              {item.reservations.length === 0 ? (
                <p className="gr-note">Nobody has reserved this yet.</p>
              ) : (
                <ul className="gr-reservations">
                  {item.reservations.map(r => (
                    <li key={r.id} className="gr-reservation">
                      <div className="gr-reservation__main">
                        <strong className="gr-wrap">{r.guest_name}</strong>
                        <span> reserved {r.quantity}</span>
                        <div className="gr-note">{formatDateTime(r.created_at)}</div>
                        {r.note && <div className="gr-wrap">{r.note}</div>}
                      </div>
                      {canEdit && (
                        <button type="button" className="header-action-btn header-btn-danger" disabled={removing === r.id}
                          aria-label={`Remove ${r.guest_name}'s reservation`} onClick={() => void removeReservation(r.id, r.guest_name)}>
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

export default GiftItemPage;
